import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES } from "../../data";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1.1, delay: 0.4 + i * 0.18, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HERO_LINES = ["The Scent", "of a Century", "Sandalwood & Saffron"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Background spotlight image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.heroSandalwood}
          alt="Aged sandalwood heartwood"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#12100E] via-[#12100E]/70 to-[#12100E]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12100E] via-transparent to-[#12100E]/40" />
      </motion.div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-sans-lux text-xs uppercase tracking-[0.35em] text-[#D49A44] mb-8"
        >
          Merchants of Rare Aromatics · Since 1911
        </motion.p>

        <h1 className="font-serif-lux font-light text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight text-[#F4EFE6] max-w-5xl">
          {HERO_LINES.map((l, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="visible"
                className={`block ${i === 2 ? "italic text-[#D49A44]" : ""}`}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-8 max-w-2xl"
        >
          <p className="font-sans-lux text-base md:text-lg text-[#B0A89D] leading-relaxed max-w-md">
            A heritage trading house dealing in the world&rsquo;s finest sandalwood and
            saffron — sourced with patience, graded with rigour, delivered with trust.
          </p>
          <a
            href="#inquire"
            data-testid="hero-cta"
            className="btn-lux shrink-0 inline-flex items-center justify-center border border-[#D49A44] px-8 py-4 font-sans-lux text-xs uppercase tracking-[0.2em] text-[#D49A44]"
          >
            Request a Quote
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.4 }}
        className="absolute bottom-8 left-6 md:left-12 z-10 flex items-center gap-3"
      >
        <div className="w-px h-12 bg-[#D49A44]/50" />
        <span className="font-sans-lux text-[10px] uppercase tracking-[0.3em] text-[#7C756B]">
          Scroll to explore
        </span>
      </motion.div>
    </section>
  );
};
